import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  Loader2,
} from 'lucide-react';

import { MonitoredUrl, MonitoringStats } from '../types/site-monitor';
import { getMonitoredUrls, getMonitoringStats, deleteMonitoredUrl } from '../services/siteMonitorApi';
import MonitoringStatsCards from '../components/site-monitor/MonitoringStatsCards';
import SiteStatusBadge from '../components/site-monitor/SiteStatusBadge';
import AddEditMonitoredUrlModal from '../components/site-monitor/AddEditMonitoredUrlModal';
import { toast } from 'react-toastify';

const PAGE_SIZE = 15;

type SortField = 'label' | 'type' | 'currentStatus' | 'ttfb' | 'seoScore' | 'pingFrequency' | 'lastPingAt';
type SortDir = 'asc' | 'desc';

const pingFreqLabel: Record<string, string> = {
  '5': '5 min',
  '15': '15 min',
  '30': '30 min',
  '60': '60 min',
};

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

function getTtfbColor(ttfb: number) {
  if (ttfb < 500) return 'text-emerald-600';
  if (ttfb < 1500) return 'text-amber-600';
  return 'text-red-600';
}

function getSeoColor(score: number) {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function getSeoTextColor(score: number) {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
}

const SiteMonitor = () => {
  const [data, setData] = useState<MonitoredUrl[]>([]);
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [dataToEdit, setDataToEdit] = useState<MonitoredUrl | null>(null);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dataToDelete, setDataToDelete] = useState<MonitoredUrl | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Search, sort, pagination
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('lastPingAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);

  const loadData = () => {
    setLoading(true);
    getMonitoredUrls()
      .then(setData)
      .finally(() => setLoading(false));
  };

  const loadStats = () => {
    setStatsLoading(true);
    getMonitoringStats()
      .then(setStats)
      .finally(() => setStatsLoading(false));
  };

  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  const handleAddData = (newData: MonitoredUrl) => {
    setData((prev) => [newData, ...prev]);
    loadStats();
  };

  const handleUpdateData = (newData: MonitoredUrl) => {
    setData((prev) => prev.map((item) => (item.id === newData.id ? newData : item)));
    loadStats();
  };

  const toggleAddEditModal = () => setIsAddEditModalOpen((v) => !v);

  const handleDeleteClick = (row: MonitoredUrl) => {
    setDataToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!dataToDelete) return;
    setDeleting(true);
    deleteMonitoredUrl(dataToDelete.id)
      .then(() => {
        setData((prev) => prev.filter((item) => item.id !== dataToDelete.id));
        toast.success('URL removida do monitoramento!');
        loadStats();
      })
      .catch(() => {})
      .finally(() => {
        setDeleting(false);
        setDeleteDialogOpen(false);
        setDataToDelete(null);
      });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Filter + sort + paginate
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let rows = data;
    if (q) {
      rows = rows.filter(
        (r) =>
          r.url.toLowerCase().includes(q) ||
          (r.label && r.label.toLowerCase().includes(q)),
      );
    }
    rows = [...rows].sort((a, b) => {
      let av: any, bv: any;
      switch (sortField) {
        case 'label':
          av = (a.label || a.url).toLowerCase();
          bv = (b.label || b.url).toLowerCase();
          break;
        case 'type':
          av = a.type;
          bv = b.type;
          break;
        case 'currentStatus':
          av = a.currentStatus;
          bv = b.currentStatus;
          break;
        case 'ttfb':
          av = a.latestPing?.ttfbMs ?? 99999;
          bv = b.latestPing?.ttfbMs ?? 99999;
          break;
        case 'seoScore':
          av = a.latestSeo?.seoScore ?? -1;
          bv = b.latestSeo?.seoScore ?? -1;
          break;
        case 'pingFrequency':
          av = Number(a.pingFrequency);
          bv = Number(b.pingFrequency);
          break;
        case 'lastPingAt':
          av = a.lastPingAt ? new Date(a.lastPingAt).getTime() : 0;
          bv = b.lastPingAt ? new Date(b.lastPingAt).getTime() : 0;
          break;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [data, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [search]);

  document.title = 'Monitoramento de Sites | Ad Pulse';

  const SortableHeader = ({
    field,
    children,
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="inline-flex items-center gap-1 hover:text-foreground transition-colors font-medium"
    >
      {children}
      <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'opacity-100' : 'opacity-40'}`} />
    </button>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MonitoringStatsCards stats={stats} loading={statsLoading} />
      </div>

      {/* Main Table */}
      <Card>
        <CardHeader className="pb-[1rem]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-xl">Monitoramento de Sites</CardTitle>
              <CardDescription className="mt-[0.25rem]">
                {filtered.length} {filtered.length === 1 ? 'site monitorado' : 'sites monitorados'}
              </CardDescription>
            </div>
            <Button onClick={() => { setDataToEdit(null); toggleAddEditModal(); }}>
              <Plus className="h-4 w-4" />
              Adicionar URL
            </Button>
          </div>

          {/* Search */}
          <div className="relative mt-[1rem] max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Procurar por URL ou label..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-[2.5rem]"
            />
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-5 flex-[3]" />
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="h-5 flex-1" />
                  <Skeleton className="h-5 flex-1" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Search className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-[1rem] text-sm font-medium text-foreground">Nenhum resultado encontrado</p>
              <p className="mt-[0.25rem] text-sm text-muted-foreground">
                {search ? 'Tente ajustar o filtro de busca.' : 'Adicione uma URL para comecar o monitoramento.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[280px]">
                      <SortableHeader field="label">URL</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="type">Tipo</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="currentStatus">Status</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="ttfb">TTFB</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="seoScore">SEO Score</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="pingFrequency">Ping</SortableHeader>
                    </TableHead>
                    <TableHead>
                      <SortableHeader field="lastPingAt">Ultimo Check</SortableHeader>
                    </TableHead>
                    <TableHead className="w-[100px] text-right">Acoes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.map((row) => (
                    <TableRow key={row.id}>
                      {/* URL */}
                      <TableCell>
                        <div className="max-w-[280px]">
                          <p className="truncate font-medium text-sm">
                            {row.label || row.url}
                          </p>
                          {row.label && (
                            <p className="truncate text-xs text-muted-foreground mt-0.5">
                              {row.url}
                            </p>
                          )}
                        </div>
                      </TableCell>

                      {/* Tipo */}
                      <TableCell>
                        <Badge variant={row.type === 'LANDING_PAGE' ? 'info' : 'warning'}>
                          {row.type === 'LANDING_PAGE' ? 'Landing Page' : 'Publisher'}
                        </Badge>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <SiteStatusBadge status={row.currentStatus} />
                      </TableCell>

                      {/* TTFB */}
                      <TableCell>
                        {row.latestPing?.ttfbMs != null ? (
                          <span className={`font-semibold text-sm ${getTtfbColor(row.latestPing.ttfbMs)}`}>
                            {row.latestPing.ttfbMs}ms
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>

                      {/* SEO Score */}
                      <TableCell>
                        {row.latestSeo?.seoScore != null ? (
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                              <div
                                className={`h-full rounded-full transition-all ${getSeoColor(row.latestSeo.seoScore)}`}
                                style={{ width: `${row.latestSeo.seoScore}%` }}
                              />
                            </div>
                            <span className={`text-sm font-semibold ${getSeoTextColor(row.latestSeo.seoScore)}`}>
                              {row.latestSeo.seoScore}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>

                      {/* Ping Freq */}
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {pingFreqLabel[row.pingFrequency] || row.pingFrequency}
                        </span>
                      </TableCell>

                      {/* Ultimo Check */}
                      <TableCell>
                        {row.lastPingAt ? (
                          <span className="text-sm text-muted-foreground">
                            {new Date(row.lastPingAt).toLocaleDateString('pt-BR', dateFormatOptions)}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Nunca</span>
                        )}
                      </TableCell>

                      {/* Acoes */}
                      <TableCell className="text-right">
                        <TooltipProvider delayDuration={200}>
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setDataToEdit(row);
                                    toggleAddEditModal();
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleDeleteClick(row)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Remover</TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-[1rem] border-t mt-[1rem]">
                  <p className="text-sm text-muted-foreground">
                    Pagina {page + 1} de {totalPages}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setPage(totalPages - 1)}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <AddEditMonitoredUrlModal
        monitoredUrl={dataToEdit}
        isOpen={isAddEditModalOpen}
        toggle={toggleAddEditModal}
        onAddData={handleAddData}
        onUpdateData={handleUpdateData}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setDataToDelete(null); } }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 mx-auto sm:mx-0">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <DialogTitle className="mt-[1rem]">Remover URL</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover{' '}
              <span className="font-medium text-foreground">
                {dataToDelete?.label || dataToDelete?.url}
              </span>{' '}
              do monitoramento? Essa acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-[0.5rem]">
            <Button
              variant="outline"
              onClick={() => { setDeleteDialogOpen(false); setDataToDelete(null); }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sim, remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteMonitor;
